import { inject, Injectable, signal } from "@angular/core";

import { firstValueFrom, Subject } from "rxjs";
import { filter, take, timeout as rxTimeout } from "rxjs/operators";

import { MarkdownService } from "@documentation/services/markdown.service";
import { DocumentEditorComponent } from "@shared/components/document-editor/component-document-editor.component";

/**
 * Provides a synchronous bridge to the active DocumentEditorComponent instance.
 *
 * Why does this exist?
 *
 * 1.  **Decoupling**: It avoids direct dependencies between document-level services (like Assistant/Chat)
 *     and the stateful `DocumentEditorComponent` view component. This prevents services needing to
 *     be aware of component lifecycle or having to manage component instances.
 *
 * 2.  **Synchronous Access**: `DocumentsStore.editedContent` is updated on a debounce timer for performance.
 *     When sending a prompt to the AI, we need the *absolute latest* editor content synchronously,
 *     without waiting for the debounce to fire. This proxy provides a `getCurrentContent()` method
 *     that directly queries the active editor component for its up-to-the-millisecond state.
 */
@Injectable({ providedIn: "root" })
export class DocumentEditorProxyService {
  // Use a signal to hold the editor instance so consumers can react if needed,
  // though primary use is the synchronous getter method.
  private readonly _activeEditor = signal<DocumentEditorComponent | null>(null);
  // Bumps whenever editor instance changes (register/unregister).
  readonly editorVersion = signal(0);
  private readonly _editorUpdated$ = new Subject<void>();

  private readonly _markdown = inject(MarkdownService);

  /**
   * Called by the `DocumentEditorComponent` when it initializes.
   */
  register(editor: DocumentEditorComponent): void {
    this._activeEditor.set(editor);
    this.editorVersion.update((v) => v + 1);
  }

  /**
   * Called by the `DocumentEditorComponent` when it is destroyed.
   */
  unregister(editor: DocumentEditorComponent): void {
    if (this._activeEditor() === editor) {
      this._activeEditor.set(null);
      this.editorVersion.update((v) => v + 1);
    }
  }

  hasActiveEditor(): boolean {
    return !!this._activeEditor();
  }

  /**
   * Synchronously gets the latest markdown content from the active editor.
   *
   * This bypasses the debounced `DocumentsStore.editedContent` and is the preferred
   * way to get content for immediate actions like sending an AI prompt.
   *
   * @returns The current markdown content, or `null` if no editor is active.
   */
  getCurrentContent(): string | null {
    try {
      return this._activeEditor()?.getCurrentContent() || null;
    } catch {
      return null;
    }
  }

  // Wait until there are no diff previews and an editor update arrives confirming the state
  async waitForNoDiffPreviewsAndStableUpdate(maxMs: number = 2000): Promise<void> {
    if (!this.hasActiveDiffPreviews()) return; // already clear
    await firstValueFrom(
      this._editorUpdated$.pipe(
        filter(() => !this.hasActiveDiffPreviews()),
        take(1),
        rxTimeout({ each: maxMs })
      )
    );
  }

  async resolveDiffsAndWait(action: "accept" | "reject"): Promise<void> {
    if (action === "accept") {
      this.acceptAllDiffPreviews();
    } else {
      this.rejectAllDiffPreviews();
    }
    await this.waitForNoDiffPreviewsAndStableUpdate();
  }

  // Removed session content getters from save path; editor is source of truth

  /**
   * Returns true if there are any diffPreview nodes present in the active editor.
   */
  hasActiveDiffPreviews(): boolean {
    try {
      const ed = this._activeEditor();
      const doc = ed?.editor?.state?.doc;
      if (!doc) return false;
      let found = false;
      doc.descendants((node: any) => {
        if (node?.type?.name === "diffPreview") {
          found = true;
          return false;
        }
        return true;
      });
      return found;
    } catch {
      return false;
    }
  }

  acceptAllDiffPreviews(): void {
    try {
      const ed = this._activeEditor();
      if (ed?.acceptAllPreviews) {
        ed.acceptAllPreviews();
      } else {
        ed?.session?.acceptAll?.();
      }
    } catch {}
  }

  rejectAllDiffPreviews(): void {
    try {
      const ed = this._activeEditor();
      if (ed?.rejectAllPreviews) {
        ed.rejectAllPreviews();
      } else {
        ed?.session?.rejectAll?.();
      }
    } catch {}
  }

  acceptDiffPreview(id: string): void {
    try {
      const ed = this._activeEditor();
      if (!ed) return;
      if (typeof (ed as any).acceptPreview === "function") {
        (ed as any).acceptPreview(id);
        return;
      }
      // Fallback: remove from session only (older editor implementations)
      ed?.session?.accept?.(id);
    } catch {}
  }

  rejectDiffPreview(id: string): void {
    try {
      const ed = this._activeEditor();
      if (!ed) return;
      if (typeof (ed as any).rejectPreview === "function") {
        (ed as any).rejectPreview(id);
        return;
      }
      // Fallback: remove from session only (older editor implementations)
      ed?.session?.reject?.(id);
    } catch {}
  }

  setMarkdown(md: string): void {
    // Important: emitUpdate must be true so DocumentEditorComponent's `onUpdate`
    // fires and propagates the markdown into DocumentsStore.editedContent.
    // Otherwise, the editor can be re-synced from store content and "undo" the change.
    this._activeEditor()?.editor?.commands.setContent(this._markdown.markdownToHtml(md ?? ""), { emitUpdate: true });
  }
}
